const API = "/api";

export type MeUser = {
  id: string;
  email: string;
  emailVerified: boolean;
};

export type MeResponse = {
  user: MeUser | null;
  isAdmin?: boolean;
  impersonator?: { id: string; email: string };
};

/** Read body as JSON; if the server returns HTML or plain text (e.g. 500), throw a clear message instead of JSON.parse errors. */
async function readJsonResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    const snippet = text.replace(/\s+/g, " ").trim().slice(0, 200);
    throw new Error(
      snippet
        ? `Server error (${res.status}): ${snippet}`
        : `Server error (${res.status})`,
    );
  }
}

async function json<T>(res: Response): Promise<T> {
  const data = (await readJsonResponse(res)) as T & { error?: string };
  if (!res.ok) {
    const msg =
      data && typeof data === "object" && typeof data.error === "string"
        ? data.error
        : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data as T;
}

export async function apiMe(): Promise<MeResponse> {
  const res = await fetch(`${API}/auth/me`, { credentials: "include" });
  const data = (await readJsonResponse(res)) as MeResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? "Failed to load session");
  }
  return {
    user: data.user ?? null,
    isAdmin: data.isAdmin === true,
    impersonator: data.impersonator,
  };
}

export type OAuthProvidersResponse = { google: boolean; apple: boolean };

export async function apiOAuthProviders(): Promise<OAuthProvidersResponse> {
  const res = await fetch(`${API}/auth/oauth/config`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Could not load sign-in options (${res.status})`);
  }
  return (await readJsonResponse(res)) as OAuthProvidersResponse;
}

export type AdminUserRow = { id: string; email: string; createdAt: number };

export async function apiAdminListUsers() {
  return json<{ users: AdminUserRow[] }>(
    await fetch(`${API}/admin/users`, { credentials: "include" }),
  );
}

export async function apiAdminImpersonate(targetUserId: string) {
  return json<{ user: MeUser | null }>(
    await fetch(`${API}/admin/impersonate`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetUserId }),
    }),
  );
}

export async function apiAdminStopImpersonate() {
  return json<{ ok: boolean }>(
    await fetch(`${API}/admin/impersonate/stop`, {
      method: "POST",
      credentials: "include",
    }),
  );
}

export async function apiAdminDeleteUser(userId: string) {
  return json<{ ok: boolean }>(
    await fetch(`${API}/admin/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export async function apiSignup(email: string, password: string) {
  const res = await fetch(`${API}/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  const data = (await readJsonResponse(res)) as { user?: MeUser; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Signup failed");
  return data;
}

export async function apiLogin(login: string, password: string) {
  // JSON.stringify drops keys whose value is `undefined`; the API must always receive both fields.
  const id = String(login ?? "").trim();
  const pw = String(password ?? "");
  const res = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ login: id, email: id, password: pw }),
  });
  const data = (await readJsonResponse(res)) as { user?: MeUser; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Login failed");
  return data;
}

export async function apiLogout() {
  await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
}

export async function apiForgotPassword(email: string) {
  const res = await fetch(`${API}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error("Request failed");
}

export async function apiResetPassword(token: string, password: string) {
  const res = await fetch(`${API}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, password }),
  });
  const data = (await readJsonResponse(res)) as { ok?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Reset failed");
}

// —— Budget API ——

export type AccountRow = {
  id: string;
  user_id: string;
  name: string;
  type: string;
  currency: string;
  sort_order: number;
  created_at: number;
  /** Lifetime ledger balance (transactions + transfers), cents. */
  balanceCents: number;
};

export type CategoryRow = {
  id: string;
  user_id: string;
  name: string;
  kind: string;
  color: string | null;
  sort_order: number;
  created_at: number;
};

export type TransactionRow = {
  id: string;
  user_id: string;
  account_id: string;
  account_name: string;
  category_id: string | null;
  category_name: string | null;
  category_kind: string | null;
  amount_cents: number;
  description: string;
  notes: string | null;
  occurred_on: string;
  created_at: number;
};

export async function apiListAccounts() {
  return json<{ accounts: AccountRow[] }>(
    await fetch(`${API}/accounts`, { credentials: "include" }),
  );
}

export async function apiCreateAccount(body: {
  name: string;
  type?: string;
  currency?: string;
  sort_order?: number;
}) {
  return json<{ account: AccountRow }>(
    await fetch(`${API}/accounts`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiUpdateAccount(
  id: string,
  body: Partial<{ name: string; type: string; currency: string; sort_order: number }>,
) {
  return json<{ account: AccountRow }>(
    await fetch(`${API}/accounts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiDeleteAccount(id: string) {
  return json<{ ok: boolean }>(
    await fetch(`${API}/accounts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export async function apiListCategories() {
  return json<{ categories: CategoryRow[] }>(
    await fetch(`${API}/categories`, { credentials: "include" }),
  );
}

export async function apiCreateCategory(body: {
  name: string;
  kind: string;
  color?: string | null;
  sort_order?: number;
}) {
  return json<{ category: CategoryRow }>(
    await fetch(`${API}/categories`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiUpdateCategory(
  id: string,
  body: Partial<{ name: string; kind: string; color: string | null; sort_order: number }>,
) {
  return json<{ category: CategoryRow }>(
    await fetch(`${API}/categories/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiDeleteCategory(id: string) {
  return json<{ ok: boolean }>(
    await fetch(`${API}/categories/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export async function apiListTransactions(params: {
  from: string;
  to: string;
  accountId?: string;
  categoryId?: string;
}) {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  if (params.accountId) q.set("accountId", params.accountId);
  if (params.categoryId) q.set("categoryId", params.categoryId);
  return json<{ transactions: TransactionRow[] }>(
    await fetch(`${API}/transactions?${q}`, { credentials: "include" }),
  );
}

export async function apiCreateTransaction(body: {
  account_id: string;
  category_id: string;
  amount: number;
  description?: string;
  notes?: string | null;
  occurred_on: string;
}) {
  return json<{ transaction: TransactionRow }>(
    await fetch(`${API}/transactions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiUpdateTransaction(
  id: string,
  body: Partial<{
    account_id: string;
    category_id: string | null;
    amount: number;
    description: string;
    notes: string | null;
    occurred_on: string;
  }>,
) {
  return json<{ transaction: TransactionRow }>(
    await fetch(`${API}/transactions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiDeleteTransaction(id: string) {
  return json<{ ok: boolean }>(
    await fetch(`${API}/transactions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export type TransferRow = {
  id: string;
  user_id: string;
  from_account_id: string;
  from_account_name: string;
  to_account_id: string;
  to_account_name: string;
  amount_cents: number;
  occurred_on: string;
  notes: string | null;
  created_at: number;
};

export type TransferDirection = "from" | "to" | "either";

export async function apiListTransfers(params: {
  from: string;
  to: string;
  accountId?: string;
  direction?: TransferDirection;
}) {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  if (params.accountId) q.set("accountId", params.accountId);
  if (params.direction && params.direction !== "either") {
    q.set("direction", params.direction);
  }
  return json<{ transfers: TransferRow[] }>(
    await fetch(`${API}/transfers?${q}`, { credentials: "include" }),
  );
}

export async function apiCreateTransfer(body: {
  from_account_id: string;
  to_account_id: string;
  amount: number;
  occurred_on: string;
  notes?: string | null;
}) {
  return json<{ transfer: TransferRow }>(
    await fetch(`${API}/transfers`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function apiDeleteTransfer(id: string) {
  return json<{ ok: boolean }>(
    await fetch(`${API}/transfers/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export type SummaryResponse = {
  month: string;
  from: string;
  to: string;
  totalIncomeCents: number;
  totalExpenseCents: number;
  netCents: number;
  /** All-time combined balance of accounts with type `savings` (transactions + transfers). */
  savingsAccountsTotalCents: number;
  byCategory: {
    categoryId: string;
    name: string;
    kind: string;
    totalCents: number;
    /** Hex fill for charts; null uses default palette. */
    color: string | null;
  }[];
};

export async function apiSummary(month: string, opts?: { excludeOffBudget?: boolean }) {
  const q = new URLSearchParams({ month });
  if (opts?.excludeOffBudget) {
    q.set("exclude_off_budget", "1");
  }
  return json<SummaryResponse>(
    await fetch(`${API}/summary?${q}`, {
      credentials: "include",
    }),
  );
}

// —— Assistant API ——

export type AssistantChatMessage = {
  role: "user" | "assistant";
  content: string;
};

export async function apiAssistantChat(messages: AssistantChatMessage[]) {
  return json<{ message: string }>(
    await fetch(`${API}/assistant/chat`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages }),
    }),
  );
}
